import { useRef, useCallback } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  setContactLocations,
  setContactLocationsStatus,
  updateSearchAreaResultTotals,
} from '@user/store/map'
import { MapPinDTO } from '@user/store/map/typings'
import { isRecordEntityType } from '@user/store/map/constants'
import {
  excludeClosedLinkedSalesCenterPins,
  excludeKnockStatusPinsCoveredByPestPac,
  excludeLinkedPestPacPins,
  fetchPestPacLocations,
  PestPacFetchResult,
  isPestPacPin,
  mergeColocatedSalesCenterAndPestPacPins,
} from '@user/store/map/api'
import { get } from '@user/store/services/crud'
import { ServiceConfig } from '@sales-center/common'
import {
  TileId,
  getTilesInViewport,
  tileToBBox,
  latLngToTileXY,
} from '@user/utils/tileUtils'

// ── Constants ─────────────────────────────────────────────────────────────────

export const TILE_TTL_MS = 5 * 60 * 1000 // 5 minutes
export const MAX_CACHE_SIZE = 500 // LRU eviction threshold
export const MIN_ZOOM_FOR_TILES = 8
// Pins at tile/viewport boundaries may land fractionally outside the reported
// viewport bounds due to floating-point rounding in cluster coordinate computation.
// 0.002° ≈ 222 m — wide enough to absorb the largest observed boundary gaps.
const VIEWPORT_EPSILON = 0.002 // degrees (~222 m at NJ latitude)

// ── Types ─────────────────────────────────────────────────────────────────────

interface TileResult {
  locations: MapPinDTO[]
  myRecords: number
  myLead: number
  myDraftLead: number
  myOpportunity: number
  // Accurate total from the backend COUNT query — unaffected by MaxDatabaseRows cap.
  // Used for the "X records found in visible area" indicator.
  // totalLead/DraftLead/Opportunity are derived from rendered pins in computeResultTotals.
  total: number
  totalLead: number
  totalDraftLead: number
  totalOpportunity: number
  // Set when the SC fetch succeeded but the PestPac fetch for this tile failed.
  // Sales Center pins are still valid and rendered — only PestPac pins are missing
  // for this tile. The cache write for this result must not treat it as a fresh
  // success (see `refresh()` below), so the tile is retried on the next viewport
  // refresh instead of silently withholding PestPac pins for the full TTL.
  pestPacFailed?: boolean
}

const EMPTY_TILE_RESULT: TileResult = {
  locations: [],
  myRecords: 0,
  myLead: 0,
  myDraftLead: 0,
  myOpportunity: 0,
  total: 0,
  totalLead: 0,
  totalDraftLead: 0,
  totalOpportunity: 0,
}

export interface TileCacheEntry {
  result: TileResult
  fetchedAt: number
  status: 'loading' | 'success' | 'error'
  promise?: Promise<TileResult>
  staleResult?: TileResult
}

export interface TileFilterParams {
  entityTypes: string // cleanEntityTypes (may be empty string)
  ownerId: string | null
  overrideFilters: boolean
  knockStatusIds: string | undefined
  pestPacPinsEnabled: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function buildFilterHash(p: TileFilterParams): string {
  const types = p.entityTypes.split('_').filter(Boolean).sort().join(',')
  const ks = (p.knockStatusIds ?? '')
    .split('_')
    .filter(Boolean)
    .sort()
    .join(',')
  return `t=${types}|o=${p.ownerId ?? 'all'}|k=${ks}|ov=${p.overrideFilters ? '1' : '0'}|pp=${p.pestPacPinsEnabled ? '1' : '0'}`
}

export function tileKey(tile: TileId, filterHash: string): string {
  return `${tile.z}/${tile.x}/${tile.y}:${filterHash}`
}

export function isFresh(entry: TileCacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TILE_TTL_MS
}

export function lruSet(
  cache: Map<string, TileCacheEntry>,
  key: string,
  value: TileCacheEntry,
): void {
  if (cache.has(key)) cache.delete(key) // re-insert = bump to most-recently-used
  cache.set(key, value)
  if (cache.size > MAX_CACHE_SIZE) {
    // Map preserves insertion order; first key = least-recently-used
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }
}

// Merges tile location groups into one deduplicated, deterministically ordered list.
// The order matters: under Pin Density "Standard" the symbol layer runs with
// icon-allow-overlap:false, and MapLibre resolves those collisions in raw feature
// order. Emitting the same pins in a different order between refreshes flips which
// pin wins and makes pins blink (SH-1840). Earlier groups win a pinId collision.
export function dedupeAndSortLocations(groups: MapPinDTO[][]): MapPinDTO[] {
  const seen = new Set<string>()
  const merged: MapPinDTO[] = []

  for (const group of groups) {
    for (const loc of group) {
      if (seen.has(loc.pinId)) continue
      seen.add(loc.pinId)
      merged.push(loc)
    }
  }

  return merged.sort((a, b) => {
    if (a.pinId < b.pinId) return -1
    if (a.pinId > b.pinId) return 1
    return 0
  })
}

export function mergeTileLocations(groups: MapPinDTO[][]): MapPinDTO[] {
  const deduped = dedupeAndSortLocations(groups)

  return mergeColocatedSalesCenterAndPestPacPins(
    deduped.filter((loc) => !isPestPacPin(loc)),
    deduped.filter(isPestPacPin),
  )
}

// ── Result totals computation ─────────────────────────────────────────────────

export interface ResultTotals {
  inViewportTotal: number // sum of pin counts — drives "X records found" indicator
  totalLead: number // from backend COUNT query — drives filter panel checkboxes
  totalDraftLead: number
  totalOpportunity: number
  myRecords: number
  myLead: number
  myDraftLead: number
  myOpportunity: number
}

function countableRecords(pin: MapPinDTO): number {
  if (isRecordEntityType(pin.entityType)) return pin.count ?? 1

  return (
    pin.entities?.filter((entity) => isRecordEntityType(entity.entityType))
      .length ?? 0
  )
}

export function computeResultTotals(
  inViewport: MapPinDTO[],
  allResults: TileResult[],
): ResultTotals {
  const sum = (
    field: Exclude<keyof TileResult, 'locations' | 'pestPacFailed'>,
  ) => allResults.reduce((acc, r) => acc + r[field], 0)

  // Pin-derived per-type counts — filter panel checkboxes show what's on the map.
  // Mixed-type multitenant clusters bundle several entity types into one pin;
  // walk their entities[] to get an accurate per-type count.
  const sumPinType = (type: string) =>
    inViewport.reduce((acc, l) => {
      const t = l.entityType?.toLowerCase()
      if (t === type) return acc + (l.count ?? 1)
      if (t === 'mixed')
        return (
          acc +
          (l.entities?.filter((e) => e.entityType?.toLowerCase() === type)
            .length ?? 0)
        )
      return acc
    }, 0)

  const totalLead = sumPinType('lead')
  const totalDraftLead = sumPinType('draftlead')
  const totalOpportunity = sumPinType('opportunity')

  // Pin-derived total — drives myRecords cap and searchAreaResult.total.
  const inViewportTotal = inViewport.reduce(
    (acc, l) => acc + countableRecords(l),
    0,
  )

  // my* (except myRecords) are capped against the backend's accurate totals, not
  // pin-derived counts, so a user with 180 my-leads isn't capped at the pin count.
  return {
    inViewportTotal,
    totalLead,
    totalDraftLead,
    totalOpportunity,
    myRecords: Math.min(sum('myRecords'), inViewportTotal),
    myLead: Math.min(sum('myLead'), sum('totalLead')),
    myDraftLead: Math.min(sum('myDraftLead'), sum('totalDraftLead')),
    myOpportunity: Math.min(sum('myOpportunity'), sum('totalOpportunity')),
  }
}

// ── Tile location filter/merge helpers ────────────────────────────────────────

export function filterLocationsNoKs(locations: MapPinDTO[]): MapPinDTO[] {
  return locations.filter(
    (loc) => !loc.knockStatusSettingColor && !loc.knockStatusSettingName,
  )
}

export function filterKsOnlyBadges(locations: MapPinDTO[]): MapPinDTO[] {
  return locations.filter((loc) => loc.entityId === 0)
}

/**
 * Merges secondary (no-KS) results into primary (KS-matched) results,
 * excluding secondary locations that:
 *  - are not real entities (entityId <= 0)
 *  - carry a KS association
 *  - share a pinId with a primary result
 *  - contain an entityId already present in the primary results (cluster overlap)
 */
export function mergeWithoutKsLocations(
  primary: MapPinDTO[],
  secondary: MapPinDTO[],
): MapPinDTO[] {
  const existingPinIds = new Set(primary.map((l) => l.pinId))
  const existingEntityIds = new Set(
    primary
      .map((l) => l.entityId)
      .filter((id): id is number => id != null && id > 0),
  )
  const withoutKs = secondary.filter(
    (loc) =>
      loc.entityId > 0 &&
      !loc.knockStatusSettingColor &&
      !loc.knockStatusSettingName &&
      !existingPinIds.has(loc.pinId) &&
      !existingEntityIds.has(loc.entityId) &&
      !loc.entities?.some(
        (e) =>
          e.entityId != null && existingEntityIds.has(e.entityId as number),
      ),
  )
  return [...primary, ...withoutKs]
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTileCache() {
  const dispatch = useAppDispatch()
  const cacheRef = useRef<Map<string, TileCacheEntry>>(new Map())
  const prevFilterHashRef = useRef<string | null>(null)
  const prevZRef = useRef<number | null>(null)
  // Increments on every refresh() call. Only the call whose token still matches
  // the current value dispatches — stale viewport calls are silently dropped.
  const refreshTokenRef = useRef(0)

  const fetchTileLocations = useCallback(
    async (tile: TileId, params: TileFilterParams): Promise<TileResult> => {
      const bbox = tileToBBox(tile.x, tile.y, tile.z)
      const ksOnly = params.entityTypes === '' && !!params.knockStatusIds
      const effectiveTypes =
        params.overrideFilters || ksOnly
          ? 'draftlead_lead_opportunity'
          : params.entityTypes
      // Governs the Sales Center entityTypes=... request only — a record-type pin (lead/opp/
      // draftlead) with no knock status is hidden by the KS filter unless entity types are also
      // selected (SH-670). PestPac pins have no entityTypes concept of their own and must NOT
      // share this gate — see includeWithoutKnockStatusForPestPac below.
      const includeWithoutKs =
        params.entityTypes !== '' && !!params.knockStatusIds

      // PestPac pins load regardless of the entity-type filter (SH-586) — a status-less PestPac
      // pin must stay visible even in "Knock Status Locations Only" mode (entityTypes === ''),
      // unlike a status-less Sales Center record. Only an explicit, non-matching knock status
      // hides a PestPac pin (still enforced server-side via knockStatusIds itself).
      const includeWithoutKnockStatusForPestPac = true

      const ownerParam = params.ownerId ? `&ownerId=${params.ownerId}` : ''
      const ksParam = params.knockStatusIds
        ? `&knockStatusIds=${params.knockStatusIds}`
        : ''
      const includeWithoutKsParam = includeWithoutKs
        ? '&includeWithoutKnockStatus=true'
        : ''

      const url =
        `${ServiceConfig.baseAPIUrl}/ContactLocations/SearchArea` +
        `?long1=${bbox.long1}&lat1=${bbox.lat1}&long2=${bbox.long2}&lat2=${bbox.lat2}` +
        `&zoom=${tile.z}&entityTypes=${effectiveTypes}` +
        ownerParam +
        ksParam +
        includeWithoutKsParam

      const [response, pestPacResult] = await Promise.all([
        get(url),
        params.pestPacPinsEnabled
          ? fetchPestPacLocations(
              bbox,
              tile.z,
              params.knockStatusIds,
              includeWithoutKnockStatusForPestPac,
            )
          : Promise.resolve<PestPacFetchResult>({ ok: true, locations: [] }),
      ])
      const pestPacLocations = pestPacResult.locations
      if (!response.ok)
        throw new Error(
          `tile ${tile.z}/${tile.x}/${tile.y}: ${response.status}`,
        )
      const data = await response.json()
      let locations = (data.locations as MapPinDTO[]) ?? []
      const myRecords = (data.myRecords as number) ?? 0
      const myLead = (data.myLead as number) ?? 0
      const myDraftLead = (data.myDraftLead as number) ?? 0
      const myOpportunity = (data.myOpportunity as number) ?? 0
      const total = (data.total as number) ?? 0
      const totalLead = (data.totalLead as number) ?? 0
      const totalDraftLead = (data.totalDraftLead as number) ?? 0
      const totalOpportunity = (data.totalOpportunity as number) ?? 0

      // Scenario 1: No KS filter and no override — hide entity pins that have an
      // associated knock status. Only records without any KS should be visible.
      // Check both color and name: color can be null for KS settings with no color
      // defined, but name is always set when a KS association exists.
      if (!params.knockStatusIds && !params.overrideFilters) {
        locations = filterLocationsNoKs(locations)
      }

      // Scenario 3: Only KS filter active — keep only standalone KS-only badges.
      // The backend returns entity pins with matching KS IDs too; discard them here.
      if (ksOnly) {
        locations = filterKsOnlyBadges(locations)
      }

      if (pestPacLocations.length > 0) {
        locations = [
          ...excludeKnockStatusPinsCoveredByPestPac(
            excludeClosedLinkedSalesCenterPins(locations, pestPacLocations),
            pestPacLocations,
          ),
          ...excludeLinkedPestPacPins(locations, pestPacLocations),
        ]
      }

      // pestPacFailed: SC pins stay valid when only PestPac failed; the flag stops
      // refresh() caching this as a fresh success (throwing blanked SC pins too, SH-1341).
      return {
        locations,
        myRecords,
        myLead,
        myDraftLead,
        myOpportunity,
        total,
        totalLead,
        totalDraftLead,
        totalOpportunity,
        pestPacFailed: !pestPacResult.ok,
      }
    },
    [],
  )

  const refresh = useCallback(
    async (
      topLeftLat: number,
      topLeftLng: number,
      bottomRightLat: number,
      bottomRightLng: number,
      zoom: number,
      filterParams: TileFilterParams,
    ): Promise<void> => {
      const filterHash = buildFilterHash(filterParams)
      const cache = cacheRef.current
      const z = Math.floor(zoom)
      // Fetch one zoom level finer than the display zoom so each tile covers 1/4 the
      // area, reducing the chance the MaxDatabaseRows cap truncates geographic coverage.
      // Only apply at zoom 11–13: below 11, tiles are too large and 4× amplification
      // generates too many requests; above 13, tile bboxes are already small enough
      // that the cap is never hit.
      const fetchZoom = z >= 11 && z < 14 ? z + 1 : z

      // Wipe cache when active filters change so stale tile data isn't reused.
      if (
        prevFilterHashRef.current !== null &&
        prevFilterHashRef.current !== filterHash
      ) {
        cache.clear()
      }
      prevFilterHashRef.current = filterHash

      prevZRef.current = z

      // Claim a token for this call. Only the most-recent call dispatches —
      // any in-flight call whose token no longer matches the current value is
      // from a superseded viewport and must not overwrite the latest result.
      const token = ++refreshTokenRef.current

      const tiles = getTilesInViewport(
        topLeftLat,
        topLeftLng,
        bottomRightLat,
        bottomRightLng,
        fetchZoom,
      )

      dispatch(setContactLocationsStatus('loading'))

      // Partition tiles into synchronous cache hits and async pending fetches.
      const cachedResults: TileResult[] = []
      const staleResults: TileResult[] = []
      const pendingPromises: Array<Promise<TileResult>> = []

      for (const tile of tiles) {
        const key = tileKey(tile, filterHash)
        const existing = cache.get(key)

        if (existing?.status === 'success' && isFresh(existing)) {
          cachedResults.push(existing.result)
          continue
        }

        if (existing?.status === 'loading' && existing.promise) {
          // Already in-flight — share the existing promise (no duplicate request)
          if (existing.staleResult) staleResults.push(existing.staleResult)
          pendingPromises.push(existing.promise)
          continue
        }

        // Cache miss or stale — fetch
        const staleResult =
          existing?.status === 'success'
            ? existing.result
            : existing?.staleResult
        if (staleResult) staleResults.push(staleResult)

        const fetchPromise = fetchTileLocations(tile, filterParams)
          .then((result) => {
            // A PestPac-only failure still renders SC pins but must not be cached
            // as fresh: fetchedAt 0 makes isFresh() false so the next viewport
            // refresh retries PestPac for this tile instead of waiting out the TTL.
            lruSet(cache, key, {
              result,
              fetchedAt: result.pestPacFailed ? 0 : Date.now(),
              status: 'success',
            })
            return result
          })
          .catch(() => {
            cache.delete(key) // remove failed entry so next viewport retries
            return EMPTY_TILE_RESULT
          })

        lruSet(cache, key, {
          result: EMPTY_TILE_RESULT,
          fetchedAt: 0,
          status: 'loading',
          promise: fetchPromise,
          staleResult,
        })
        pendingPromises.push(fetchPromise)
      }

      // Phase 1: some tiles are already cached, others still loading — dispatch
      // the cached subset immediately so the overlapping region feels instant
      // during pan rather than waiting for every new tile to arrive.
      if (
        pendingPromises.length > 0 &&
        (cachedResults.length > 0 || staleResults.length > 0)
      ) {
        const partial = mergeTileLocations([
          ...cachedResults.map((r) => r.locations),
          ...staleResults.map((r) => r.locations),
        ])
        dispatch(setContactLocations(partial))
      }

      try {
        const resolvedResults = await Promise.all(pendingPromises)

        // A newer refresh() call is already in flight — discard these results.
        if (token !== refreshTokenRef.current) return

        // Full result: synchronous cache hits + newly resolved fetches
        const allResults = [...cachedResults, ...resolvedResults]

        // Merge and deduplicate by pinId (pins near tile edges appear in two tiles)
        //
        // Co-located Sales Center + PestPac pins are folded here, AFTER the per-tile results have
        // been deduplicated. Doing it per tile made a pin's id depend on whether that tile's PestPac
        // fetch happened to succeed, so an edge-straddling record could arrive under two different
        // ids and survive the dedupe twice (SH-1327).
        const merged = mergeTileLocations(allResults.map((r) => r.locations))

        dispatch(setContactLocations(merged))

        // Filter to the viewport bbox for filter-panel counts. A small epsilon
        // handles pins that land fractionally outside the viewport bounds due to
        // floating-point rounding in tile/cluster coordinate computation.
        // KS-only badges (entityId === 0) are map decorations, not countable records.
        const inViewport = merged.filter(
          (l) =>
            l.entityId !== 0 &&
            l.latitude <= topLeftLat + VIEWPORT_EPSILON &&
            l.latitude >= bottomRightLat - VIEWPORT_EPSILON &&
            l.longitude >= topLeftLng - VIEWPORT_EPSILON &&
            l.longitude <= bottomRightLng + VIEWPORT_EPSILON,
        )
        const totals = computeResultTotals(inViewport, allResults)
        // Pin count uses the same inViewport set as entity counts so both numbers
        // stay consistent. KnockStatus-only location pins are excluded.
        const displayedPinCount = inViewport.filter(
          (l) => l.entityType?.toLowerCase() !== 'knockstatus',
        ).length
        dispatch(
          updateSearchAreaResultTotals({
            total: totals.inViewportTotal,
            totalLead: totals.totalLead,
            totalDraftLead: totals.totalDraftLead,
            totalOpportunity: totals.totalOpportunity,
            myRecords: totals.myRecords,
            myLead: totals.myLead,
            myDraftLead: totals.myDraftLead,
            myOpportunity: totals.myOpportunity,
            locations: inViewport,
            displayedPinCount,
          }),
        )
      } catch {
        if (token !== refreshTokenRef.current) return
        dispatch(setContactLocationsStatus('error'))
      }
    },
    [dispatch, fetchTileLocations],
  )

  const evictTile = useCallback(
    (lat: number, lng: number, zoom: number): void => {
      const cache = cacheRef.current
      const iz = Math.floor(zoom)
      // Evict at zoom and ±1 — user may be at a different zoom when save completes
      for (const z of [iz - 1, iz, iz + 1]) {
        if (z < 1) continue
        const { x, y } = latLngToTileXY(lat, lng, z)
        const prefix = `${z}/${x}/${y}`
        cache.forEach((_, key) => {
          if (key.startsWith(prefix)) cache.delete(key)
        })
      }
    },
    [],
  )

  const evictAll = useCallback((): void => {
    cacheRef.current.clear()
  }, [])

  return { refresh, evictTile, evictAll }
}
